/* ========================================
 * Typography for ResourceType.HTML content
 *
 * Editorial reading UX: PT Serif headings, Lato body,
 * calm contrast, professional documentation feel.
 * Scoped to HTML resource rendering only — other resource
 * types (documentGenerator, checklist, etc.) use their own styles.
 *
 * The .content element-level resets (h1–h6, p, ul, table, etc.)
 * live in @layer article-base so that component CSS modules
 * (Table, Booktags, Xbox, etc.) automatically win the cascade
 * without needing specificity hacks.
 * ======================================== */

/* --- Document title --- */

/* Base: title scrolls with content (fallback for Safari etc.) */
.title {
    position: relative;
    margin: 0;
    padding: 1.5rem 0 0.75rem;
    font-family: "PT Serif", Georgia, "Times New Roman", serif;
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e3a47;
    line-height: 1.25;
    letter-spacing: -0.02em;
    text-wrap: balance;

    @container (max-width: $mantine-breakpoint-sm) {
        font-size: 1.375rem;
        padding: 1rem 0 0.5rem;
    }
}

/* Progressive enhancement: sticky + scroll-driven morph */
@supports (animation-timeline: scroll()) {
    .title {
        position: sticky;
        top: 0;
        z-index: 1;
        background: white;
        border-bottom: 2px solid transparent;
        transform-origin: left top;

        animation: title-shrink linear both;
        animation-timeline: --page-scroll;
        animation-range: 0px 120px;
    }

    @keyframes title-shrink {
        /* 0–42% (~50px): title stays full size */
        0%, 42% {
            transform: scale(1);
            width: 100%;
            padding: 1.5rem 0 0.75rem;
            border-bottom-color: transparent;
        }
        /* 42–100% (50–120px): morph to compact
           width: 100%/0.6 compensates for scale shrinking the box */
        100% {
            transform: scale(0.6);
            width: 166.667%;
            padding: 0.75rem 0 0.5rem;
            border-bottom-color: var(--mantine-color-gneis-1);
        }
    }
}

/* --- Scroll-to-top button (inside title) --- */

/* Hidden by default (Safari fallback: no sticky header, no button) */
.scrollToTop {
    display: none;
}

@supports (animation-timeline: scroll()) {
    .scrollToTop {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        right: 0;
        top: 50%;
        width: 2rem;
        height: 2rem;
        padding: 0;
        border: none;
        background: transparent;
        color: var(--mantine-color-gneis-6);
        cursor: pointer;
        opacity: 0;
        pointer-events: none;
        /* Counter-scale to offset parent's scale(0.6) → 1.667 * 0.6 = 1.0 */
        transform: translateY(-50%) scale(1.667);
        transition:
            background var(--transition-duration-fast) var(--transition-ease-smooth),
            color var(--transition-duration-fast) var(--transition-ease-smooth);

        animation: scroll-top-reveal steps(1) both;
        animation-timeline: --page-scroll;
        animation-range: 119px 120px;

        & svg {
            width: 1rem;
            height: 1rem;
        }

        &:hover {
            color: var(--mantine-color-gneis-8);
        }
    }

    @keyframes scroll-top-reveal {
        0% {
            opacity: 0;
            pointer-events: none;
        }
        100% {
            opacity: 1;
            pointer-events: auto;
        }
    }
}

/* --- Document description / lede --- */

.description {
    font-family: "PT Serif", Georgia, "Times New Roman", serif;
    font-size: 1.0625rem;
    font-style: italic;
    line-height: 1.6;
    color: #5a6b6b;
    padding: 1rem 0 1.25rem;
    border-bottom: 1px solid var(--mantine-color-gneis-1);
    margin-bottom: 0.25rem;

    p {
        margin: 0 0 0.375rem;
    }

    p:last-child {
        margin-bottom: 0;
    }

    @container (max-width: $mantine-breakpoint-sm) {
        font-size: 1rem;
        padding: 0.75rem 0 1rem;
    }
}

@layer article-base {
.content {
    /* Base typography */
    font-family: var(--article-font-body);
    font-size: 1rem;
    line-height: 1.7;
    color: var(--article-text);
    overflow-x: clip;

    /* --- Headings --- */

    h1, h2, h3, h4, h5, h6 {
        font-family: var(--article-font-heading);
        font-weight: 700;
        color: var(--article-heading);
        margin-top: 0;
        margin-bottom: 0;
        text-wrap: pretty;
    }

    h1 {
        font-size: 1.625rem;
        line-height: 1.3;
        letter-spacing: -0.025em;
        margin-top: 2.5rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--article-border);
    }

    h2 {
        font-size: 1.375rem;
        line-height: 1.35;
        margin-top: 2rem;
        margin-bottom: 0.75rem;
    }

    h3 {
        font-size: 1.1875rem;
        line-height: 1.4;
        margin-top: 1.75rem;
        margin-bottom: 0.625rem;
    }

    h4 {
        font-size: 1.0625rem;
        line-height: 1.45;
        margin-top: 1.5rem;
        margin-bottom: 0.5rem;
    }

    h5 {
        font-size: 1rem;
        line-height: 1.5;
        letter-spacing: 0;
        margin-top: 1.25rem;
        margin-bottom: 0.375rem;
    }

    h6 {
        font-size: 0.875rem;
        line-height: 1.5;
        letter-spacing: 0.01em;
        text-transform: uppercase;
        margin-top: 1.25rem;
        margin-bottom: 0.375rem;
    }

    /* --- Paragraphs --- */

    p {
        margin-top: 0;
        margin-bottom: 0.875rem;
        text-wrap: pretty;
    }

    section > p:last-child {
        margin-bottom: 0;
    }

    /* --- Sections --- */

    @supports (animation-timeline: scroll()) {
        section:global(.level) {
            scroll-margin-top: 7rem;
        }
    }

    /* --- Links: shared base ---
     * Default appearance = teal nav-link. Component CSS modules
     * (AutoLink, PanelLink, File, etc.) override as needed.
     * This layer always loses to unlayered component styles. */

    a {
        color: var(--article-link);
        text-decoration: underline;
        text-decoration-color: color-mix(in srgb, var(--article-link) 35%, transparent);
        text-underline-offset: 0.15em;
        text-decoration-thickness: 1px;
        transition: color var(--transition-duration-fast) var(--transition-ease-smooth),
                    text-decoration-color var(--transition-duration-fast) var(--transition-ease-smooth);

        &:hover {
            color: var(--article-link-hover);
            text-decoration-color: var(--article-link-hover);
        }

        &:focus-visible {
            outline: 2px solid var(--mantine-color-gneis-9);
            outline-offset: 2px;
            border-radius: 2px;
        }
    }

    /* --- Lists --- */

    ul, ol {
        margin-top: 0;
        margin-bottom: 0.875rem;
        padding-left: 1.5rem;
    }

    li {
        margin-bottom: 0.125rem;
    }

    li:last-child {
        margin-bottom: 0;
    }

    li > ul, li > ol {
        margin-top: 0.375rem;
        margin-bottom: 0;
    }

    /* --- Tables --- */

    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 1rem;
        margin-bottom: 1rem;
        font-size: 0.875rem;
        line-height: 1.5;
    }

    th, td {
        padding: 0.5rem 0.75rem;
        text-align: left;
        border-bottom: 1px solid var(--article-border);
        vertical-align: top;
    }

    th {
        font-weight: 700;
        font-family: var(--article-font-body);
        color: var(--article-heading);
        background-color: var(--article-bg-subtle);
        font-size: 0.8125rem;
        letter-spacing: 0.01em;
    }

    tbody tr:nth-child(even) {
        background-color: var(--article-border-subtle);
    }

    tbody tr:last-child td,
    tbody tr:last-child th {
        border-bottom: none;
    }

    /* --- Inline formatting --- */

    strong {
        font-weight: 700;
        color: var(--article-heading);
    }

    /* --- Images --- */

    img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 1rem auto;
        border-radius: var(--mantine-radius-sm);
    }

    /* --- Horizontal rules --- */

    hr {
        border: none;
        border-top: 1px solid var(--article-border);
        margin: 2rem 0;
    }

    /* --- Responsive (small screens) --- */

    @container (max-width: $mantine-breakpoint-sm) {
        font-size: 0.9375rem;
        line-height: 1.65;

        h1 {
            font-size: 1.375rem;
            margin-top: 2rem;
        }

        h2 {
            font-size: 1.1875rem;
            margin-top: 1.5rem;
        }

        h3 {
            font-size: 1.0625rem;
            margin-top: 1.25rem;
        }

        h4 { font-size: 1rem; }
        h5, h6 { font-size: 0.9375rem; }

        ul, ol {
            padding-left: 1.25rem;
        }

        table { font-size: 0.8125rem; }
        th, td { padding: 0.375rem 0.5rem; }
    }
}
} /* @layer article-base */
